import axios from "axios";
import { ACTIVITY_HEADER, interactionAgeMs } from "../utils/activity";

const api = axios.create({ baseURL: window.location.origin });

/** Authenticated download. Pulls filename from the server's Content-Disposition.
 *
 * Note the error handling: with responseType "blob" an error RESPONSE is also a
 * Blob, so `err.response.data.detail` is undefined and every caller's catch
 * shows a generic message instead of the reason the server gave. Reading the
 * blob back as text and re-attaching the parsed body means a failed download
 * says what actually went wrong.
 */
export async function downloadFile(
  url: string,
  fallbackName: string,
  params?: object,
  /** For endpoints that take the request as a BODY rather than a query string.
   *  The ad-hoc report download posts a whole report definition — columns,
   *  filters, sort — which does not belong in a URL. Kept as a separate
   *  argument so the existing GET callers are untouched. */
  options?: { method?: "get" | "post"; data?: unknown },
) {
  let r;
  try {
    r =
      options?.method === "post"
        ? await api.post(url, options.data ?? {}, { responseType: "blob", params })
        : await api.get(url, { responseType: "blob", params });
  } catch (err: any) {
    const body = err?.response?.data;
    if (body instanceof Blob) {
      try {
        const text = await body.text();
        err.response.data = JSON.parse(text);
      } catch {
        // Not JSON — a proxy error page, say. Keep whatever text we got.
        err.response.data = { detail: await body.text().catch(() => "") };
      }
    }
    throw err;
  }
  const cd = (r.headers["content-disposition"] || "") as string;
  const m = /filename="([^"]+)"/.exec(cd);
  const fn = m?.[1] || fallbackName;
  const contentType = (r.headers["content-type"] as string) || "application/octet-stream";
  const blob = new Blob([r.data], { type: contentType });
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u;
  a.download = fn;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(u), 500);
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Report how long since the user last actually touched the machine, so the
  // server's idle timeout measures a human rather than our own polling. Omitted
  // entirely when no interaction has been observed — the server treats a
  // missing header as "not user-driven", which is what a background poll is.
  const age = interactionAgeMs();
  if (age !== null) config.headers[ACTIVITY_HEADER] = String(age);
  // Global customer scoping: the header customer switcher is the single source
  // of truth. When a customer is selected, scope every GET to it so all screens
  // obey the selection — unless the call already specifies its own customer_id.
  // (Endpoints that don't declare customer_id simply ignore the extra param,
  // so this is safe to apply blanket.)
  try {
    const cid = localStorage.getItem("current_customer_id");
    const method = (config.method || "get").toLowerCase();
    // A call site that hard-codes ?customer_id=... in the URL string has
    // ALREADY declared its scope, but config.params is empty for it — so the
    // check below used to append a SECOND customer_id. FastAPI takes the LAST
    // value for a scalar query param (verified against the live API), which
    // meant the header selection silently overrode the page's own customer:
    // opening customer B while the switcher pointed at A returned A's devices.
    // Honour whichever scope the caller already expressed, in params OR in URL.
    const inUrl = /[?&]customer_id=/.test(config.url || "");
    if (cid && method === "get" && !inUrl) {
      const params = (config.params || {}) as Record<string, unknown>;
      if (params.customer_id == null) params.customer_id = Number(cid);
      config.params = params;
    }
  } catch {
    /* localStorage unavailable — non-fatal */
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    if (status === 401) {
      localStorage.removeItem("token");
      // A "session_superseded" 401 means this account signed in somewhere else
      // (single-session enforcement). Stash a flag so the login page can explain
      // the sign-out instead of showing a bare login form.
      // Explain WHY the session ended, so the login page can say so instead of
      // showing a bare form: signed in elsewhere, or timed out while idle.
      const detail = err.response?.data?.detail;
      if (
        detail === "session_superseded" ||
        detail === "session_idle_timeout" ||
        detail === "session_revoked"
      ) {
        try {
          sessionStorage.setItem("signout_reason", detail);
        } catch {
          /* sessionStorage unavailable — non-fatal */
        }
      }
      // Only redirect if we're on a protected page; never bounce when already on /login
      // (the login page itself fetches public branding which 401s before auth).
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    } else if (status === 402) {
      // License enforcement (backend returns 402 in three shapes). Translate
      // each into a UI signal via window events; LicenseContext / LicenseGate /
      // LicenseNotices listen. We still reject so component-level catches run.
      const data = (err.response?.data || {}) as { detail?: unknown; license_status?: string };
      const detail = data.detail;
      if (detail === "license_locked") {
        // Hard lock — flip the whole app into the lock screen.
        window.dispatchEvent(
          new CustomEvent("license:locked", { detail: { status: data.license_status } }),
        );
      } else if (detail === "license_read_only") {
        // Grace period — reads work, this mutation was blocked.
        window.dispatchEvent(
          new CustomEvent("license:notice", {
            detail: {
              message:
                "License is in its grace period — read-only. Renew the license to make changes again.",
            },
          }),
        );
      } else if (typeof detail === "string") {
        // Feature-not-licensed or a cap limit — surface the server's message.
        window.dispatchEvent(new CustomEvent("license:notice", { detail: { message: detail } }));
      }
    }
    return Promise.reject(err);
  },
);

export default api;
